import { useState, useEffect } from 'react'

/**
 * Hook to detect user's motion preference for accessibility
 * Respects prefers-reduced-motion media query
 * 
 * Phase 1: Foundation & Architecture
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    // Check initial preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches)
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    
    // Legacy support
    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  return prefersReducedMotion
}

/**
 * Helper to get animation config based on reduced motion preference
 */
export function useAnimationConfig() {
  const reducedMotion = useReducedMotion()

  return {
    // If reduced motion, animations are instant (0 duration)
    spring: reducedMotion 
      ? { type: 'tween', duration: 0 } 
      : { type: 'spring', stiffness: 400, damping: 30 },
    
    fade: reducedMotion
      ? { duration: 0 }
      : { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
    
    scale: reducedMotion
      ? { duration: 0 }
      : { type: 'spring', stiffness: 500, damping: 25 },
    
    // Canvas animation values
    floatAmplitude: reducedMotion ? 0 : 4,
    floatDuration: reducedMotion ? 0 : 3,
    pulseSpeed: reducedMotion ? 0 : 1,
    
    // Reduced motion flag
    reducedMotion,
  }
}
